import React from 'react'
import { ApproverRequestDetails } from '../../../store/slices/Approver/approverRequestDetailsSlice'
import ApprovedTimeOffForm from '../ApprovalForms/ApprovedTimeOffForm'
import DeniedTimeOffForm from '../ApprovalForms/DeniedTimeOffForm'
import HoldTimeOffForm from '../ApprovalForms/HoldTimeOffForm'
import PartiallyApprovedTimeOffForm from '../ApprovalForms/PartiallyApprovedTimeOffForm'
import ReturnedTimeOffForm from '../ApprovalForms/ReturnedTimeOffForm'
import RetractedDeniedForm from '../ApprovalForms/RetractedDeniedForm'
import { ButtonStateConfig } from '../ApprovalForms/buttonStateUtils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  ApproverStatusChangeFlag,
  changeApproverRequestStatus,
} from '../../../store/slices/Approver/approverStatusChangeSlice'
import { useNavigate } from 'react-router-dom'
import Loader from '../../../components/common/Loader'
import ValidationBanner from '../../../components/common/ValidationBanner'
import { decodeComment } from '../../../utils/statusFieldsUtils'
import { trackFormSubmit, trackFormError } from '../../../services/analytics'
import {
  APPROVAL_UNAVAILABLE_MSG,
  OVERRIDE_NOT_AVAILABLE_MSG,
  FULL_APPROVAL_UNAVAILABLE_MSG,
  FPD_MSG,
} from '../ProcessTimeOffRequest/ApprovalMessages'
import { BACKEND_UNAVAILABLE_MESSAGE } from '../../../utils/apiError'
import { isRequestDateBeforeToday } from '../../../utils/date'

interface TableRow {
  id: string
  date: string
  day: string
  times: string
  requestedHours: string
  isOverlapped?: boolean
}

interface RequestDetailsActionsProps {
  data: ApproverRequestDetails
  tableRows: TableRow[]
  isPastRequest: boolean
  isSubmittedRequest: boolean
  isOnHoldRequest: boolean
  isRetractionRequest: boolean
  isPartialRetractionRequest: boolean
  isDeniedRequest: boolean
  isOverrideDeniedRequest: boolean
  activeForm: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  setActiveForm: (
    form: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  ) => void
  buttonStateConfig: ButtonStateConfig
  onApprovedChange?: (payload: { byRow: { id: string; requestedHours: number; approvedHours: number }[]; totalApprovedHours: number }) => void
  /** Called when the active form selection changes (approved/denied/hold/etc.) */
  onActiveFormChange?: (form?: string) => void
  /**
   * Optional mapping provided by the container that represents the current
   * approved hours per request-day id. Forwarded to the partial-approval form
   * so it can initialize its checkbox state and preserve selections.
   */
  approvedHoursById?: Record<string, number>
}
//helper function to date format
const convertDateToYYYYMMDD = (date?: string) => {
  if (!date) return ''

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date
  }

  // Convert MM/DD/YYYY → YYYY-MM-DD
  const [month, day, year] = date.split('/')

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const getApprovalAlert = (
  isPartialApprovalAllowed: boolean,
  isFullApprovalAllowed: boolean,
  isOverrideAllowed: boolean,
  currentStatusValue: string
) => {
  if (!isPartialApprovalAllowed && !isFullApprovalAllowed) {
    return APPROVAL_UNAVAILABLE_MSG
  }

  if (
    ['DENIED', 'PDENIED'].includes(currentStatusValue) &&
    !isOverrideAllowed
  ) {
    return OVERRIDE_NOT_AVAILABLE_MSG
  }

  if (!isFullApprovalAllowed) {
    return FULL_APPROVAL_UNAVAILABLE_MSG
  }

  return ''
}

/**
 * Renders the approver status-change error banner. Backend/gateway failures
 * (500/502/503/504, normalized to BACKEND_UNAVAILABLE_MESSAGE by
 * extractApiErrorMessage) get the standard "contact support" banner used
 * everywhere else this message appears; every other rejection reason is
 * shown as-is, as before.
 */
const StatusChangeErrorBanner: React.FC<{ error: string }> = ({ error }) => {
  if (error === BACKEND_UNAVAILABLE_MESSAGE) {
    return (
      <ValidationBanner
        variant="error"
        heading="Could not process the time off request"
        message={
          <p className="p3 -book m-0!">
            If the problem continues after re-logging in, email the National
            TORT Support team at{' '}
            <a
              href="mailto:NAT-TORT@kp.org"
              className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
            >
              NAT-TORT@kp.org
            </a>{' '}
            with your name, employee ID, NUID, computer screenshots, and a
            description of the issue.
          </p>
        }
      />
    )
  }
  return (
    <ValidationBanner
      variant="error"
      heading={error}
      message={
        error.includes('simultaneously updated by another user')
          ? undefined
          : 'Please try again.'
      }
    />
  )
}

/**
 * RequestDetailsActions component — renders the appropriate action form
 * based on current request status and active form selection
 */
const RequestDetailsActions: React.FC<RequestDetailsActionsProps> = ({
  data,
  tableRows,
  isPastRequest,
  isSubmittedRequest,
  isOnHoldRequest,
  isRetractionRequest,
  isPartialRetractionRequest,
  isDeniedRequest,
  isOverrideDeniedRequest,
  activeForm,
  setActiveForm,
  buttonStateConfig,
  onApprovedChange,
  onActiveFormChange,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const showDeniedForm = isDeniedRequest && !isPastRequest
  // Survives the form swap that happens when switching status forms, so
  // "Approved" stays checked after ApprovedTimeOffForm remounts.
  const [selectedStatus, setSelectedStatus] = React.useState('')

  const { loading, error } = useAppSelector(
    (state) => state.approverStatusChange
  )
 
  const currentStatus = data.statuses?.[0]

  const currentStatusValue = data.statuses?.[0]?.status?.toUpperCase() || ''

  // Retractions/partial retractions must remain processable by the approver
  // regardless of past dates (both retracted-before-start and retracted-after-
  // last-date requests), so this whole family is excluded from date disabling.
  const retractedStatuses = [
    'RTSUBMITTED',
    'RTSUBMITTED_RELEASED',
    'RTSUBMITTED_ONHOLD',
    'PRTSUBMITTED',
    'PRTSUBMITTED_RELEASED',
    'PRTSUBMITTED_ONHOLD',
  ]
  // isRequestDateBeforeToday compares plain YYYY-MM-DD strings so the result
  // never depends on the viewer's timezone (see its doc comment in
  // utils/date.ts for the EST/IST bug this replaced).
  const hasPastRequestedDate =
    data.requestedDates?.some((d) => isRequestDateBeforeToday(d.date)) ?? false
  const isPastDateDisabled = hasPastRequestedDate && !retractedStatuses.includes(currentStatusValue)
  const showPastDateBanner = isPastDateDisabled

  const {
    isPartialApprovalAllowed,
    isFullApprovalAllowed,
    isOverrideAllowed,
  } = data

const approvalAlert = getApprovalAlert(
  isPartialApprovalAllowed,
  isFullApprovalAllowed,
  isOverrideAllowed,
  currentStatusValue
)

  const fpdMessage =  FPD_MSG

  const returnedAllowedStatuses = [
    'SUBMITTED',
    'SUBMITTED_RELEASED',
    'RESUBMITTED',
    'RESUBMITTED_RELEASED',
    'SUBMITTED_ONHOLD',
    'RESUBMITTED_ONHOLD',
  ]

  const isReturnedAllowed = returnedAllowedStatuses.includes(currentStatusValue)

  // const isDeniedOverrideStatus = ['RTDENIED',
  // 'PRTDENIED',
  // 'RTDENIED_OVERRIDDEN',
  // 'PRTDENIED_OVERRIDDEN',
  // 'TORPAPPROVEDRETRACTIONDENIED',].includes(
  //   currentStatusValue
  // )
  const enableOverrideSubmitButton =
    buttonStateConfig.enableOverrideSubmitButton ?? false

  const disableOverrideSubmitButton =
    buttonStateConfig.showOverrideForm === true && !enableOverrideSubmitButton

  const holdDetails =
    isOnHoldRequest && currentStatus
      ? {
          holdPlacedOn: currentStatus.createdTime || '',
          holdPlacedBy:
            currentStatus.firstName && currentStatus.lastName
              ? `${currentStatus.firstName} ${currentStatus.lastName}`
              : currentStatus.fullName || 'System',
          approverComments: decodeComment(currentStatus.comments) || '',
        }
      : undefined
  const handleStatusChange = (
    status: string,
    defaultForm: 'approved' | 'denied' = 'approved'
  ) => {
    const disabledMap: Record<string, boolean> = {
      approved: buttonStateConfig.approvedDisabled,
      denied: buttonStateConfig.deniedDisabled,
      hold: buttonStateConfig.holdDisabled,
      returned: buttonStateConfig.returnedDisabled,
      partiallyapproved: buttonStateConfig.partiallyApprovedDisabled,
    }

    if (disabledMap[status]) {
      return
    }

    setSelectedStatus(status)

    const formMap: Record<string, typeof activeForm> = {
      denied: 'denied',
      hold: 'hold',
      partiallyapproved: 'partiallyapproved',
      returned: 'returned',
      approved: 'approved',
    }

    // Keep the denied form mounted so selecting "Approved" does not swap out
    // the radio form before its local selected status is updated.
    const nextForm =
      (isDeniedRequest || activeForm === 'denied') && status === 'approved'
        ? 'denied'
        : (formMap[status] || defaultForm)

    setActiveForm(nextForm)

    dispatch({ type: 'approverStatusChange/clearError' })
    // Reset approved-hours mapping on any status change so the Current
    // Request table immediately reflects Requested Hours for all rows.
    if (onApprovedChange && tableRows && tableRows.length > 0) {
      const byRow = tableRows.map((r) => ({
        id: r.id,
        requestedHours: Number(r.requestedHours) || 0,
        approvedHours: Number(r.requestedHours) || 0,
      }))
      const totalApprovedHours = byRow.reduce((s, rr) => s + rr.approvedHours, 0)
      onApprovedChange({ byRow, totalApprovedHours })
    }
    // Notify container to clear any transient mapping as well (defensive)
    onActiveFormChange?.(nextForm)
  }

  const commonFormProps = {
    numberOfDates: data.requestedDates.length,
    dateRequested: data.startDate,
    activeForm,
    disableEndDate: data.endDate,
  }

  const getFlag = (action: 'approved' | 'denied'): ApproverStatusChangeFlag => {
    const status = data.statuses?.[0]?.status?.toUpperCase() || ''

    if (action === 'approved') {
      if (
        [
          'RTDENIED',
          'PRTDENIED',
          'RTDENIED_OVERRIDDEN',
          'OVERRIDDEN_RTDENIED',
          'TORPAPPROVEDRETRACTIONDENIED',
          'PAPPROVED_RTDENIED',
        ].includes(status)
      ) {
        return 'RO'
      }

      if (['DENIED', 'PDENIED'].includes(status)) {
        return 'O'
      }

      return 'A'
    }

    return 'D'
  }

  const handleStatusSubmit = async (
    flag: ApproverStatusChangeFlag,
    payload: any
  ) => {
    try {
      const requestPayload: any = {
        TOReqId: data.toReqId,
        approveDenyHoldReleaseFlag: flag,
        approveDenyComments: payload.comments,
      }

      if (flag === 'D') {
        requestPayload.approverDenialReason = payload.denialReason
      }

      // Hold payload fields
      if (flag === 'H') {
        requestPayload.heldUntilDate = convertDateToYYYYMMDD(
          payload.heldUntil ?? payload.heldUntilDate
        )

        requestPayload.holdStartDate = convertDateToYYYYMMDD(
          payload.holdFrom ?? payload.holdStartDate
        )
      }

      // Partial approval payload fields
      if (flag === 'P') {
        requestPayload.approverDenialReason = payload.denialReason

        requestPayload.tortPartialApprovedChecked = payload.approvedDates

        requestPayload.tortPartialApprovedUnChecked = payload.deniedDates

        // Include any overlapped IDs reported by the UI so backend can process them
        requestPayload.tortPartialApprovedOverlapped = payload.overlappedDates || []

      }

      await dispatch(changeApproverRequestStatus(requestPayload)).unwrap()

      trackFormSubmit('approver-status-change', { flag })
      navigate('/approver')
    } catch (error) {
      trackFormError(['approver-status-change-failed'], 'approver-status-change')
      console.error(error)
    }
  }

  const renderApprovedForm = (extraProps: { isDisabledDueToDate?: boolean; [key: string]: unknown } = {}) => (
    <ApprovedTimeOffForm
      {...commonFormProps}
      {...extraProps}
      isDisabledDueToDate={(extraProps.isDisabledDueToDate ?? false) || isPastDateDisabled}
      isApprovedSelected={selectedStatus === 'approved'}
      tableRows={tableRows}
      onSubmit={(payload) => handleStatusSubmit(getFlag('approved'), payload)}
      onCancel={() => navigate('/approver')}
      onStatusChange={handleStatusChange}
      buttonStateConfig={buttonStateConfig}
      requestId={data.toReqId}
      enableOverrideSubmitButton={enableOverrideSubmitButton}
      disableOverrideSubmitButton={disableOverrideSubmitButton}
      toTypeCd={data?.timeOffAllocations[0].toTypeCd}
    />
  )

const renderDeniedForm = (extraProps: { isDisabledDueToDate?: boolean; [key: string]: unknown } = {}) => {
    return (
    <DeniedTimeOffForm
      {...commonFormProps}
      {...extraProps}
      isDisabledDueToDate={(extraProps.isDisabledDueToDate ?? false) || isPastDateDisabled}
      isDenied={isDeniedRequest}
      buttonStateConfig={buttonStateConfig}
      onSubmit={(payload) =>
        handleStatusSubmit(
          payload.status === 'approved'
            ? getFlag('approved')
            : getFlag('denied'),
          payload
        )
      }
      onCancel={() =>navigate('/approver')}
      onStatusChange={handleStatusChange}
      enableOverrideSubmitButton={enableOverrideSubmitButton}
      disableOverrideSubmitButton={disableOverrideSubmitButton}
      isReturnedAllowed={isReturnedAllowed}
      toTypeCd={data?.timeOffAllocations[0].toTypeCd}

    />
  )}
  const renderHoldForm = (extraProps: { isDisabledDueToDate?: boolean; [key: string]: unknown } = {}) => (
    <HoldTimeOffForm
      {...commonFormProps}
      {...extraProps}
      isDisabledDueToDate={(extraProps.isDisabledDueToDate ?? false) || isPastDateDisabled}
      onSubmit={(payload) => handleStatusSubmit('H', payload)}
      onCancel={() => navigate('/approver')}
      onStatusChange={handleStatusChange}
      buttonStateConfig={buttonStateConfig}
      holdUntilMaxDate={data.holdUntilMaxDate}
      onHoldEndDate={data?.statuses?.[0]?.onHoldEndDate}
      onHoldStartDate={data?.statuses?.[0]?.onHoldStartDate}
      isHoldAllowed={data.isHoldAllowed}
      toTypeCd={data?.timeOffAllocations[0].toTypeCd}
    />
  )

  const renderPartialApprovedForm = (extraProps: { isDisabledDueToDate?: boolean } = {}) => (
    <PartiallyApprovedTimeOffForm
      {...commonFormProps}
      tableRows={tableRows}
      buttonStateConfig={buttonStateConfig}
      onSubmit={(payload) => handleStatusSubmit('P', payload)}
      onCancel={() => navigate('/approver')}
      onStatusChange={handleStatusChange}
      isDisabledDueToDate={(extraProps.isDisabledDueToDate ?? false) || isPastDateDisabled}
      onApprovedChange={onApprovedChange}
      toTypeCd={data?.timeOffAllocations[0].toTypeCd}
    />
  )
  
  const renderReturnedForm = (extraProps: { isDisabledDueToDate?: boolean } = {}) => (
    <ReturnedTimeOffForm
      {...commonFormProps}
      buttonStateConfig={buttonStateConfig}
      onSubmit={(payload) => handleStatusSubmit('X', payload)}
      onCancel={() => navigate('/approver')}
      onStatusChange={handleStatusChange}
      toTypeCd={data?.timeOffAllocations[0].toTypeCd}
      isDisabledDueToDate={(extraProps.isDisabledDueToDate ?? false) || isPastDateDisabled}
    />
  )
  if (loading) {
    return <Loader />
  }

  return (
    <div>
      {/* Normal Submitted Flow */}
      {error && (
        <div className="mt-3 max-w-md">
          <StatusChangeErrorBanner error={error} />
        </div>
      )}
      {approvalAlert && (
        <div className="mt-4">
          <ValidationBanner variant="error" heading={approvalAlert} />
        </div>
      )}

      {showPastDateBanner && (
        <div className="mt-4">
          <ValidationBanner
            variant="error"
            message="Time Off Requests which contain a past calendar date cannot be processed by the approver."
          />
        </div>
      )}

      {data?.timeOffAllocations?.some((allocation) => allocation.toTypeCd === 'D') &&
        currentStatusValue !== 'RTSUBMITTED' &&
        (activeForm === 'denied' || activeForm === 'partiallyapproved') && (
        <div className="mt-4 mb-4!">
          <ValidationBanner variant="info" heading={fpdMessage} />
        </div>
      )}

      {/* Retraction and partial-retraction statuses also satisfy isSubmittedRequest/
          isOnHoldRequest, so excluding them here keeps the 3 flows below mutually
          exclusive and stops the same form rendering twice for those statuses */}
      {isSubmittedRequest && !isRetractionRequest && !isPartialRetractionRequest && (
        <>
          {activeForm === 'approved' &&
            renderApprovedForm({
              isDisabledDueToDate: isPastRequest && !isSubmittedRequest,
            })}

          {activeForm === 'denied' &&
            renderDeniedForm({
              isDisabledDueToDate: isPastRequest,
            })}

          {activeForm === 'hold' &&
            renderHoldForm({
              isDisabledDueToDate: isPastRequest,
            })}

          {!buttonStateConfig.hidePartialApprovalOption &&
            activeForm === 'partiallyapproved' &&
            renderPartialApprovedForm()}

          {activeForm === 'returned' && renderReturnedForm()}
        </>
      )}

      {/* On Hold Flow */}
      {isOnHoldRequest && !isRetractionRequest && !isPartialRetractionRequest && (
        <>
          {activeForm === 'hold' &&
            renderHoldForm({
              isOnHold: true,
              holdDetails,
              isDisabledDueToDate: isPastDateDisabled,
            })}

          {activeForm === 'denied' &&
            renderDeniedForm({
              isDenied: false,
              isDisabledDueToDate: isPastDateDisabled,
            })}

          {activeForm === 'approved' &&
            renderApprovedForm({
              isDisabledDueToDate: isPastDateDisabled,
            })}

          {!buttonStateConfig.hidePartialApprovalOption &&
            activeForm === 'partiallyapproved' &&
            renderPartialApprovedForm({ isDisabledDueToDate: isPastDateDisabled })}

          {activeForm === 'returned' && renderReturnedForm({ isDisabledDueToDate: isPastDateDisabled })}
        </>
      )}

      {/* Retraction / Partial Retraction — never disabled due to past dates */}
      {(isRetractionRequest || isPartialRetractionRequest) && (
        <>
          {activeForm === 'approved' &&
            renderApprovedForm({
              isRetracted: true,
              isDisabledDueToDate: false,
            })}

          {activeForm === 'denied' &&
            renderDeniedForm({
              isRetracted: true,
              isDisabledDueToDate: false,
            })}

          {activeForm === 'hold' &&
            renderHoldForm({
              isRetracted: true,
              isOnHold: isOnHoldRequest,
              holdDetails,
              isDisabledDueToDate: false,
            })}

          {!buttonStateConfig.hidePartialApprovalOption &&
            activeForm === 'partiallyapproved' &&
            renderPartialApprovedForm({ isDisabledDueToDate: false })}
        </>
      )}

      {/* Denied */}
      {isDeniedRequest &&
        showDeniedForm &&
        activeForm === 'denied' &&
        renderDeniedForm()}


      {/* Override Denied */}
      {buttonStateConfig.showOverrideForm &&
        isOverrideDeniedRequest &&
        activeForm === 'approved' && (
          <RetractedDeniedForm
            onSubmit={(payload) =>
              handleStatusSubmit(getFlag('approved'), payload)
            }
            onCancel={() => navigate('/approver')}
          />
        )}
    </div>
  )
}

export default RequestDetailsActions